package dev.lingvo.ops.pages

import androidx.compose.runtime.Composable
import androidx.compose.runtime.DisposableEffect
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.attr
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.rememberPageContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import dev.lingvo.ops.components.OpsGoogleSignIn
import dev.lingvo.ops.state.OpsLoginIntent
import dev.lingvo.ops.state.OpsLoginStore
import dev.lingvo.site.api.Session
import dev.lingvo.site.ds.LAlert
import dev.lingvo.site.ds.LAlertVariant
import dev.lingvo.site.ds.LButton
import dev.lingvo.site.ds.LTextField
import dev.lingvo.site.ds.Space
import dev.lingvo.site.ds.TypeScale
import dev.lingvo.site.ds.toSitePalette
import kotlinx.browser.document
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.onSubmit
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Form
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

@Page("/login")
@Composable
fun LoginPage() {
    val ctx = rememberPageContext()
    val scope = rememberCoroutineScope()
    val store = remember {
        OpsLoginStore(
            scope = scope,
            initialFailure = when (ctx.route.queryParams["oauth_error"]) {
                "operator_denied" -> "This Google account is not authorized for the operator console."
                "access_denied" -> "Google sign-in did not finish. Try again or use your password."
                else -> null
            },
            signIn = { email, password ->
                Session.login(email, password)
                if (!Session.isAuthenticated) {
                    Session.logout()
                    error("Session validation failed")
                }
            },
            onSignedIn = { ctx.router.navigateTo("/") },
        )
    }
    DisposableEffect(store) { onDispose { store.dispose() } }
    LaunchedEffect(Unit) { document.title = "Sign in · Lingvo operator console" }
    val state = store.state
    val palette = ColorMode.current.toSitePalette()
    OpsAuthSurface("Sign in", "Use your operator account. Your session is separate from the Lingvo product.") {
        Column(Modifier.fillMaxWidth().gap(Space.MD)) {
            OpsGoogleSignIn()
            P(Modifier.color(palette.textMuted).fontSize(TypeScale.Small).margin(0.px).toAttrs()) {
                Text("Email and password")
            }
            Form(attrs = Modifier.fillMaxWidth().attr("aria-busy", state.submitting.toString()).toAttrs {
                onSubmit { it.preventDefault(); store.accept(OpsLoginIntent.Submit) }
                onKeyDown {
                    if (it.key == "Enter") {
                        it.preventDefault()
                        store.accept(OpsLoginIntent.Submit)
                    }
                }
            }) {
                Column(Modifier.fillMaxWidth().gap(Space.MD)) {
                    LTextField(state.email, { store.accept(OpsLoginIntent.EmailChanged(it)) }, label = "Email",
                        modifier = Modifier.fillMaxWidth(), inputType = InputType.Email, autocomplete = "username", enabled = !state.submitting)
                    LTextField(state.password, { store.accept(OpsLoginIntent.PasswordChanged(it)) }, label = "Password",
                        modifier = Modifier.fillMaxWidth(), inputType = InputType.Password, autocomplete = "current-password", enabled = !state.submitting)
                    state.failure?.let { LAlert("Could not sign in", it, variant = LAlertVariant.DANGER, live = true) }
                    LButton("Sign in", { store.accept(OpsLoginIntent.Submit) }, modifier = Modifier.fillMaxWidth(),
                        loading = state.submitting, enabled = !state.submitting && !state.completed)
                }
            }
            P(Modifier.color(palette.textMuted).fontSize(TypeScale.Small).margin(0.px).toAttrs()) {
                Text("Access is checked before the console opens.")
            }
        }
    }
}
